import { ChangeDetectionStrategy, Component, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { DatepickerIntervalComponent } from "@components/datepicker-interval/datepicker-interval.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent, ListComponent as ListComponent_1 } from "@components/list/list.component";
import { SelectorComponent } from "@components/selector/selector.component";
import moment from "moment";
import { firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";

import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { LeveransQuery } from "~app/common/models/integration/viol3/LeveransQuery";
import { CommandService } from "~app/common/services/cqs/command.service";
import { Utils } from "~app/common/services/utils";
import { DateUtils } from "~app/common/utils/date-utils";
import { SystemadministrationService } from "../../../systemadministration.service";
import { RecalculateLeveransRedovisningsdatumRelationshipCommand } from "../commands/RecalculateLeveransRedovisningsdatumRelationshipCommand";
import { IntegrationService } from "../service/integration.service";
import { LeveransRowVm } from "./leveranser.models";

@Component({
    selector: "rask-leveranser",
    templateUrl: "./leveranser.component.html",
    styleUrls: ["./leveranser.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        BlockComponent,
        SelectorComponent,
        FormsModule,
        DatepickerIntervalComponent,
        InputComponent,
        ButtonComponent,
        ListComponent_1,
        ListColumnDirective
    ]
})
export class LeveranserComponent {
    kallaList = [
        "Alla",
        "Viol2",
        "Viol3"
    ];
    leveranser: LeveransRowVm[] = [];
    selectedMottagningsplats?: any;
    query: LeveransQuery = new LeveransQuery("Alla");
    @ViewChild("leveranserList") searchResultTable: ListComponent;
    selectedRows: LeveransRowVm[] = [];

    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly integrationService = inject(IntegrationService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly systemadministrationService = inject(SystemadministrationService);
    private readonly toastService = inject(ToastService);

    constructor() {
        this.query.periodFrom = moment().startOf("day").add(-7, "day").format(DateUtils.dateformat);
        this.query.periodTom = moment().startOf("day").format(DateUtils.dateformat);
    }

    datakallaFormatter = (item) => item;

    datakallaviewFormatter = (item) => item;

    download(leveransnummer: string) {
        this.busyService.addWork(this.systemadministrationService.getLeveransJson(leveransnummer))
            .subscribe(data => {
                Utils.download(data, `${leveransnummer}.json`);
            });
    }

    exportJsonFromLeverans() {
        const levNr = this.selectedRows.length === 1 ? this.selectedRows[0].leveransnummer : this.query.leveransnummer;
        if (!levNr) {
            this.toastService.add("Ange ett leveransnummer eller markera en rad för att exportera JSON");
            return;
        }

        this.busyService.addWork(this.systemadministrationService.getLeveransJson(levNr))
            .subscribe(json => {
                Utils.download(json, `${levNr}.json`);
            });
    }

    fetchLeveranser() {
        this.busyService
            .addWork(this.integrationService.getLeveranser(this.query))
            .subscribe(res => {
                this.leveranser = res.map(x => new LeveransRowVm(x));
            });
    }

    mottagningsplatsSelectedFormatter = (item) => {
        this.selectedMottagningsplats = item;
        return item?.id;
    };

    mottagningsplatsViewFormatter = (item) => item.mottagningsplatsnummer ? `${item.mottagningsplatsnummer}: ${item.namn}` : item.namn;

    onCheckedChange() {
        this.selectedRows = this.searchResultTable.getSelectedRows();
    }

    omsandningLeveransaggregat() {
        let dialogMessage = "Vill du sända om leveransmätningarna?";

        if (this.selectedRows.length > 0) {
            const count = this.selectedRows.length;
            dialogMessage = `Vill du sända om ${count} markerad${count === 1 ? " leveransmätning" : "e leveransmätningar"}?`;
        } else if (this.query.leveransnummer) {
            dialogMessage = `Vill du sända om leveransmätning för leverans ${this.query.leveransnummer}?`;
        } else if (this.query.periodFrom && this.query.periodTom) {
            dialogMessage = `Vill du sända om leveransmätningar för perioden ${this.query.periodFrom}–${this.query.periodTom}?`;
        }

        this.raskModalService.confirm(new ModalSettings("Omsändning av leveransmätningar", dialogMessage))
            .subscribe((res) => {
                if (res === ConfirmDialogResponse.save) {
                    if (this.selectedRows.length > 0) {
                        for (const row of this.selectedRows) {
                            const cmd = {
                                $type: "ResendLeveransRedovisningsdatumCommand",
                                leveransnummer: row.leveransnummer
                            };
                            this.pushCommand(cmd).subscribe(() => {
                                this.toastService.add(`Leverans ${row.leveransnummer} har skickats om`);
                            });
                        }
                        return;
                    }

                    if (this.query.leveransnummer) {
                        const cmd = {
                            $type: "ResendLeveransRedovisningsdatumCommand",
                            leveransnummer: this.query.leveransnummer
                        };

                        this.pushCommand(cmd).subscribe(() => {
                            this.toastService.add(`Leverans ${this.query.leveransnummer} har skickats om`);
                        });
                        return;
                    }

                    if (this.query.periodFrom && this.query.periodTom) {
                        const endDate = moment(this.query.periodTom).add(1, "day").format(DateUtils.dateformat);
                        const fromDate = moment(this.query.periodFrom).format(DateUtils.dateformat);
                        const cmd = {
                            $type: "ResendLeveransRedovisningsdatumCommand",
                            from: fromDate,
                            tom: endDate
                        };

                        this.pushCommand(cmd).subscribe(() => {
                            this.toastService.add("Leveransmätningar har skickats om");
                        });
                    }
                }
            });
    }

    pushCommand(cmd: any) {
        this.commandService.addTransactionCommand(cmd);
        const obs = this.commandService.pushCommandQueueTransaction();
        const res = this.busyService.addWork(obs);

        obs.subscribe(() => {
            this.commandService.reset();
        });

        return res;
    }

    async rerunLeveranser() {
        let dialogMessage = "Vill du räkna om leveranserna?";

        if (this.selectedRows.length > 0) {
            const count = this.selectedRows.length;
            dialogMessage = `Vill du räkna om ${count} markerad${count === 1 ? " leverans" : "e leveranser"}?`;
        } else if (this.query.leveransnummer) {
            dialogMessage = `Vill du räkna om leverans ${this.query.leveransnummer}?`;
        } else if (this.query.periodFrom && this.query.periodTom) {
            const mplNummer = this.selectedMottagningsplats?.namn ?? this.query.mottagningsplatsId;
            const mplText = this.query.mottagningsplatsId ? `, mottagningsplats ${mplNummer}` : "";
            dialogMessage = `Vill du räkna om leveranser för perioden ${this.query.periodFrom}–${this.query.periodTom}${mplText}?`;
        }

        this.raskModalService.confirm(new ModalSettings("Omräkna leveranser", dialogMessage))
            .subscribe(async (res) => {
                if (res === ConfirmDialogResponse.save) {
                    const cmds = this.selectedRows.map(x => {
                        const newCmd = new RecalculateLeveransRedovisningsdatumRelationshipCommand();
                        newCmd.leveransnummer = x.leveransnummer;
                        return newCmd;
                    });

                    if (cmds.length > 0) {
                        for (const cmdval of cmds) {
                            await firstValueFrom(this.integrationService.postJob(cmdval));
                            this.toastService.add(`Leverans ${cmdval.leveransnummer} har omräknats`);
                        }
                        return;
                    }

                    const cmd = new RecalculateLeveransRedovisningsdatumRelationshipCommand();
                    cmd.periodFrom = this.query.periodFrom;
                    cmd.periodTom = this.query.periodTom;
                    cmd.leveransnummer = this.query.leveransnummer;
                    cmd.mottagningsplatsId = this.query.mottagningsplatsId;
                    const job = this.integrationService.postJob(cmd);
                    this.busyService
                        .addWork(job)
                        .subscribe(() => this.toastService.add("Leveranserna har omräknats"));
                }
            });
    }

    searchForMottagningsplatsTypeAhead = (input: string) => this.integrationService.getForMottagningsplatsTypeAhead(input, null);

    setSlut = (date: any) => {
        this.query.periodTom = date;
    };

    setStart = (date: any) => {
        this.query.periodFrom = date;
    };
}
